<template>
  <VueFinalModal
    :esc-to-close="true"
    class="flex justify-center items-center"
    content-class="relative flex flex-col w-[600px] mx-auto bg-white border rounded-lg"
  >
    <div class="p-6 bg-gray-100" >
      <div class="text-xl text-center pb-4">Village Location Selection</div>
      <div>
        <ul class="list-reset flex">
          <li @click='selectedTab=idx'
            v-for="tab, idx in tabs"
            class="tab-base"
            :class="{'tab-active':selectedTab===idx, 'tab-not-active':selectedTab!=idx}"
            :key="tab">
            {{tab}}
          </li>
        </ul>
      </div>
      <div v-show='selectedTab===0' class='tab-page'>
        <siloLocationSelector
          :levels="levels"
          :countryCode="countryIso"
          :location="initialOptions"
          v-on:on-value-change="onNewValue">
        </siloLocationSelector>
      </div>
      <div v-show='selectedTab===1' class='tab-page'>
        <h3 class="header"> Pick a point on the map </h3>

        <GoogleMap
          :api-key="apiKey"
          :center="center"
          :zoom="7"
          style="width:100%;  height: 500px;"
          @click="mark"
        >
          <Marker
            :key="index"
            v-for="(m, index) in markers"
            :position="m.position"
            :clickable="true"
            @click="center=m.position"
            :options="{ position: m.position}"
          >
          </Marker>
        </GoogleMap>

        <div class="standard-field-wrapper py-2" v-if="lastLevel">
          <label for="lastLevel.name">{{lastLevel.label}}</label>
          <div class="standard-input-wrapper">
            <div class="standard-input-container">
              <siloSelect
                :level="lastLevel.admin_level.toString()"
                :fieldName="lastLevel.name"
                :countryCode="countryIso"
                :partOf="selectedMapPoint"
                :partOfRequired="true"
                v-on:on-value-change="onNewValue">
              </siloSelect>
            </div>
          </div>
        </div>
      </div>
      <div class="float-right pt-4">
        <button :class="(admin4==='') ? 'opacity-50 btn btn-primary' : 'btn btn-primary'" @click="update" :disabled="admin4===''">Update</button>
        <button class="btn btn-secondary" @click="closeModal()">Close</button>
      </div>
      <div class="h-10">&nbsp;</div>
    </div>
  </VueFinalModal>
</template>

<script setup>
  import { inject, onMounted, ref, computed } from 'vue'
  import { VueFinalModal } from 'vue-final-modal'
  import siloSelect from '../components/siloSelect.vue'
  import siloCalloutService from '@services/siloCalloutService'
  import siloLocationSelector from '../components/siloLocationSelector.vue'
  import axios from "axios"

  const apiKey = inject('apiKey')

  const props = defineProps({
    params: Object,
  })

  const emit = defineEmits(['close'])

  let churchId = ref('')
  let levels = ref([])
  let countryIso = ref('')
  let admin1 = ref('')
  let admin2 = ref('')
  let admin3 = ref('')
  let admin4 = ref('')
  let error = ref(false)
  let selectedTab = ref(0)
  let center = ref({ lat: 45.508, lng: -73.587 })
  let markers = ref([])
  let places = ref([])
  let lat = ref('')
  let lng = ref('')
  let tabs = ref(['Lookup', 'Pick From Map'])
  let selectedMapPoint = ref('')
  let initialOptions = ref({})

  onMounted(() => {
    if (props.params.data.country) {
      levels.value = props.params.data.country.silo_details.levels
      countryIso.value = props.params.data.country.silo_details.iso_country_code
      churchId.value = props.params.data.church_id
      initialOptions.value = { type: 'id', value: props.params.data.silo_location }
    }

    if (props.params.data && props.params.data.pointData && props.params.data.pointData.training_center.location_lat && props.params.data.pointData.training_center.location_long) {
      center.value = {
        lat: parseFloat(props.params.data.pointData.training_center.location_lat),
        lng: parseFloat(props.params.data.pointData.training_center.location_long)
      }

      markers.value.push({ position: center.value })
    } else if (props.params.data.country) {
      const initPlacesService = () => {
        if (typeof google !== 'undefined' && google.maps && google.maps.places) {
          let request = {
            query: props.params.data.country.name,
            fields: ['name', 'geometry'],
          }

          let service = new google.maps.places.PlacesService(document.createElement('div'))

          service.findPlaceFromQuery(request, (results, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
              center.value = {
                lat: results[0].geometry.location.lat(),
                lng: results[0].geometry.location.lng()
              }
            }
          })
        } else {
          // If Google Maps is not loaded yet, retry after a short delay
          setTimeout(initPlacesService, 100)
        }
      }

      initPlacesService()
    } else {
      geolocate()
    }
  })

  const lastLevel = computed(() => {
    return levels.value && levels.value.length > 0 ? levels.value[levels.value.length - 1] : null
  })


  function tabClass (idx) {
    if (selectedTab.value === idx){
      return "border-gray-400"
    } else {
      return "bg-white border-r border-t inline-block rounded-t-lg pb-2 px-4 text-blue-600 font-semibold hover:text-blue-500 border-gray-200"
    }
  }

  function closeModal () {
    emit('close')
  }

  function onNewValue(v) {
    if (v.field === 'admin1') {
      admin1.value = v.value
    }

    if (v.field === 'admin2') {
      admin2.value = v.value
    }

    if (v.field === 'admin3') {
      admin3.value = v.value
    }

    if (v.field === 'admin4') {
      admin4.value = v.value
    }
  }

  function update() {
    let el = document.getElementById("silo-location-field")
    if (el) {
      el.value = admin4.value
      var event = new Event('change')
      el.dispatchEvent(event)
    } else {
      updateAndClose()
    }

    closeModal()
  }

  async function updateAndClose() {
    let dt = new Date()
    try {
      await axios.put(`/churches/${churchId.value}.json`, {
        silo_location: admin4.value,
        silo_state: 'pending',
        silo_updated_at: dt.toISOString(),
        return_path: `/churches/${churchId.value}`,
      })
    } catch(error) {
      window.Rollbar.error('Error occured updating Church', error)
      alert('Error occured updating Church')
    }
    finally {
      closeModal()
    }
  }

  function mark (event) {
    lat.value = event.latLng.lat()
    lng.value = event.latLng.lng()

    center.value = {
      lat: lat.value,
      lng: lng.value
    }

    markers.value.pop()
    markers.value.push({ position: center.value })
    selectedMapPoint.value = `latitude:${lat.value.toString()},longitude:${lng.value.toString()}`
  }

  const geolocate = async () => {
    // use built in navigator to get current location
    navigator.geolocation.getCurrentPosition(position => {
      center.value = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      }

      markers.value.push({ position: center.value })
    })
  }
</script>

<style lang="scss">
  .vm--modal {
    overflow: initial !important;
  }

  .bold {
    font-weight: bold;
  }

  .pad {
    margin: 5px;
  }
  .tab-base {
    @apply bg-white border-l border-r border-t border-gray-300 inline-block rounded-t-lg pb-2 px-4 text-blue-600 font-semibold
  }
  .tab-active {
    @apply border-t-2 border-r-2
  }
  .tab-not-active {
    @apply hover:text-blue-500
  }
  .tab-page {
    @apply bg-white rounded-r-lg rounded-b-lg p-4 border-l border-r border-b
  }
</style>
